import { ArchiveRestoreStatus } from '@mimir/common/dto/itemPropTypes';
import type { Logger } from '@mimir/common/types/logger';
import { exceptionToString } from '@mimir/common/util/exception';
import type { ColdStoragePollOps, ItemArchiveInfo, RestoredItem } from './coldStoragePollOps';


interface PromiseInfo {
  promise: Promise<RestoredItem>;
  resolve: (item: RestoredItem) => void;
}


/**
 * This implementation hides away the item polling logic so that consumers of the
 * ColdStoragePollOps abstraction can just work with a simple promise to await.
 */
export class ColdStoragePollImpl implements ColdStoragePollOps {

  private restoreProgressTimerId: number | undefined;
  private itemIdsToPromiseInfo: Record<string, PromiseInfo> = {};

  constructor(
    private logger: Logger,
    private pollDelayInMs: number,
    private getItems: (itemIds: string[]) => Promise<ItemArchiveInfo[]>,
    // Since getItems() does support support returning the highres url,
    // we need another function to obtain it once getItems() tells us
    // that an item is restored
    private getItemHighresUrl: (itemId: string) => Promise<string | undefined>,
    private setInterval: (callback: () => void, md: number) => any,
    private clearInterval: (handle: any) => void,
  ) {}


  async waitForRestore(itemId: string): Promise<RestoredItem> {
    if (this.itemIdsToPromiseInfo[itemId] !== undefined) {
      // If there is already a promise, let's return it
      return this.itemIdsToPromiseInfo[itemId].promise;
    }

    let resolveFn: (item: RestoredItem) => void;
    const promise = new Promise<RestoredItem>((resolve) => {
      resolveFn = resolve;
    });
    this.itemIdsToPromiseInfo[itemId] = { promise, resolve: resolveFn! };
    this.startPolling();
    return promise;
  }


  private startPolling() {
    if (this.restoreProgressTimerId !== undefined) {
      // Polling is already in prrogress
      return;
    }

    this.restoreProgressTimerId = this.setInterval(() => {
      void this.pollForProgress();
    }, this.pollDelayInMs);
  }


  private stopPolling() {
    if (this.restoreProgressTimerId !== undefined) {
      this.clearInterval(this.restoreProgressTimerId);
      this.restoreProgressTimerId = undefined;
    }
  }


  private async pollForProgress() {
    const itemsIds = Object.keys(this.itemIdsToPromiseInfo);
    if (itemsIds.length === 0) {
      // No need to keep polling
      this.stopPolling();
      return;
    }

    try {
      const items = await this.getItems(itemsIds);
      for (const item of items) {
        if (item.archiveInfo
          && item.archiveInfo.restoreStatus === ArchiveRestoreStatus.Completed
          && item.archiveInfo.mediaOnlineUntil
          && new Date(item.archiveInfo.mediaOnlineUntil) > new Date()) {
          // If the network is slow, we could have several pollForProgress() in flight
          // concurrently, which means that an item could have already been taken
          // care of by the time getItems() returns
          const highRes = await this.getItemHighresUrl(item.id);
          const promiseInfo = this.itemIdsToPromiseInfo[item.id];
          if (promiseInfo) {
            promiseInfo.resolve({ ...item, highRes });
            delete this.itemIdsToPromiseInfo[item.id];
          }
        }
      }
    } catch (err: any) {
      this.logger.error(`Error while getting restore progress for items: ${itemsIds.join(', ')}: ${exceptionToString(err)}`);
    }
  }

}
